import { Box, Typography } from "@mui/material";
import KlassenTile from "./KlassenTile";
import { useMemo } from "react";
import { useAppContext } from "../../../2_ksbHub/context/appContext";
import { useSondertagContext } from "../context/sondertagContext";

export default function JahrgangGrid({ angebote, onTileClick, viewMode }) {
  const { isMobile, isSmallScreen } = useAppContext();
  const { selectedSonderEvent } = useSondertagContext();

  /**
   * =========================================================
   * ✅ BUILD MAP PER HALBTAG
   * =========================================================
   */
  const { vormittagMap, nachmittagMap } = useMemo(() => {
    const vMap = {};
    const nMap = {};

    angebote.forEach((a) => {
      const key = a.klasseId.toLowerCase();

      if (a.halbtag === "vormittag") {
        vMap[key] = a;
      } else if (a.halbtag === "nachmittag") {
        nMap[key] = a;
      }
    });

    return {
      vormittagMap: vMap,
      nachmittagMap: nMap,
    };
  }, [angebote]);

  /**
   * =========================================================
   * ✅ GET ALL KLASSEN IDS
   * =========================================================
   */
  const klassenIds = useMemo(() => {
    const all = new Set(angebote.map((a) => a.klasseId));
    return angebotSort([...all]);
  }, [angebote]);

  /**
   * =========================================================
   * ✅ GRID RENDER HELPER
   * =========================================================
   */
  const minWidth = klassenIds.length > 10 ? 110 : 140;
  const renderGridRow = (map, halbtag) => {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`,
          justifyContent: "start",
          gap: 1,
          mt: 0.5,
        }}
      >
        {klassenIds.map((klasseId) => {
          const angebot = map[klasseId.toLowerCase()] || null;

          // ✅ NO ANGEBOT → render EMPTY SPACE (NOT tile)
          if (!angebot) {
            return (
              <Box
                key={`${klasseId}-${halbtag}`}
                sx={{
                  height: 56, // ✅ same height as tile
                }}
              />
            );
          }

          // ✅ ANGEBOT EXISTS → render tile
          return (
            <KlassenTile
              key={`${klasseId}-${halbtag}`}
              klasseId={klasseId}
              angebot={angebot}
              halbtag={halbtag}
              onTileClick={onTileClick}
              viewMode={viewMode}
            />
          );
        })}
      </Box>
    );
  };

  /**
   * =========================================================
   * ✅ FINAL RENDER
   * =========================================================
   */
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        mt: 0.8,
        ml: 0.8,
      }}
    >
      {/* ✅ VORMITTAG */}
      <Box>
        <Typography variant="caption" sx={{ opacity: 0.6, mb: 0.5 }}>
          {selectedSonderEvent.label.toLowerCase() === "schulreise"
            ? ""
            : "Vormittag"}
        </Typography>
        {renderGridRow(vormittagMap, "vormittag")} {/* ✅ */}
      </Box>

      {/* ✅ NACHMITTAG */}
      {selectedSonderEvent.label.toLowerCase() === "schulreise" &&
      Object.keys(nachmittagMap).length === 0 ? (
        <></>
      ) : (
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.6, mb: 0.5 }}>
            {selectedSonderEvent.label.toLowerCase() === "schulreise"
              ? ""
              : "Nachmittag"}
          </Typography>
          {renderGridRow(nachmittagMap, "nachmittag")} {/* ✅ */}
        </Box>
      )}
    </Box>
  );
}

/**
 * =========================================================
 * ✅ SORT KLASSEN (1a, 1b, 2a…)
 * =========================================================
 */
function angebotSort(arr) {
  return [...arr].sort((a, b) => {
    const parse = (id) => {
      const match = id.toLowerCase().match(/^(\d+)([a-z]+)/);
      return {
        stufe: Number(match?.[1] || 0),
        letter: match?.[2] || "",
      };
    };

    const A = parse(a);
    const B = parse(b);

    if (A.stufe !== B.stufe) return A.stufe - B.stufe;
    return A.letter.localeCompare(B.letter);
  });
}
